import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import * as crawlerApi from 'api/crawlerApi';
import * as menuApi from 'api/menuApi';
import { REGISTRATION_STEP } from 'constants/registrationStep';
import { useToast } from 'hooks/useToast';
import { useAppDispatch, useAppSelector } from 'store/hooks/useStore';
import { loaderActions } from 'store/reducers/loaderReducer/loaderSlice';
import {
  menuDetailActions,
  menuDetailSelectors,
} from 'store/reducers/menuCategoryReducer/menuDetailSlice';
import { Crawler } from 'types/crawler';
import { MenuCustomer } from 'types/menuCustomer';
import {
  mapFormFieldsToMenuEditPayload,
  mapMenuToFormFields,
  MenuEditFormFields,
} from '../types/menuEditFormFields';
import { useListStore } from './useListStore';
import { useMenuCrawlers } from './useMenuCrawlers';

/**
 * メニュー詳細 store の実装をコンポーネントから隠すための中間層
 * @returns selector と action のラッパー
 */
export const useMenuDetailStore = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const selectedMenu = useAppSelector(menuDetailSelectors.selectedMenu);
  const { fetchMenuCategories } = useListStore();
  const currentModalStep = useAppSelector(menuDetailSelectors.currentModalStep);
  const [crawlers, setCralwers] = useState<Crawler[]>([]);
  const [menuCustomers, setMenuCustomers] = useState<MenuCustomer[]>([]);
  const [canDelete, setCanDelete] = useState(false);

  const {
    menuCrawlers,
    setMenuCrawlers,
    onAddCrawler,
    onDeleteCrawler,
    onSortCrawler,
  } = useMenuCrawlers();

  const form = useForm<MenuEditFormFields>({
    defaultValues: mapMenuToFormFields(selectedMenu || {}, {}),
    mode: 'all',
  });
  const { getValues, reset, handleSubmit } = form;

  useEffect(() => {
    if (selectedMenu) {
      void (async () => {
        const result = await menuApi.fetchMenuCrawlers(selectedMenu.id);
        if (result.ok) {
          const targetCrawlers = result.value.map(({ crawlerId }) =>
            crawlerId.toString(),
          );

          reset(mapMenuToFormFields(selectedMenu, { targetCrawlers }));
          setCanDelete(targetCrawlers.length === 0);

          setMenuCrawlers(result.value);
        } else {
          toast({
            status: 'error',
            description: `クローラーの取得に失敗しました: ${result.err.message}`,
          });
        }
      })();
    } else {
      setCralwers([]);
      setCanDelete(false);
    }
  }, [selectedMenu, reset, toast, setMenuCrawlers]);

  const onCloseModal = useCallback(() => {
    dispatch(menuDetailActions.initialize());
  }, [dispatch]);

  const onOpenModalDelete = useCallback(() => {
    dispatch(menuDetailActions.updateIsOpenModalDelete(true));
  }, [dispatch]);

  const onCloseModalWhenCompleted = () => {
    onCloseModal();
    void fetchMenuCategories();
  };

  const onConfirm = () => {
    dispatch(menuDetailActions.updateStep(REGISTRATION_STEP.CONFIRMATION));
  };

  const onGoBack = () => {
    dispatch(menuDetailActions.updateStep(REGISTRATION_STEP.INPUT));
  };

  const onSubmit = handleSubmit(
    async (values: MenuEditFormFields) => {
      if (selectedMenu) {
        dispatch(loaderActions.setLoading(true));
        const payload = {
          ...mapFormFieldsToMenuEditPayload(values),
          categoryId: selectedMenu.categoryId,
        };

        const result = await menuApi.editMenu(selectedMenu.id, payload);
        if (result.ok) {
          const menuId = result.value.id;
          const payloadMenu = menuCrawlers.map((crawler) => ({
            ...crawler,
            menuId,
          }));

          const { ok } = await menuApi.editMenuCrawlers(menuId, payloadMenu);
          if (ok) {
            dispatch(menuDetailActions.updateStep(REGISTRATION_STEP.RESULT));
          } else {
            toast({
              status: 'error',
              description: 'メニューに紐づくクローラーの更新に失敗しました。',
            });
            onCloseModalWhenCompleted();
          }
          onCloseModalWhenCompleted();
        } else {
          toast({
            status: 'error',
            description: 'メニューの更新に失敗しました。',
          });
        }
        dispatch(loaderActions.setLoading(false));
      }
    },
    () => {
      toast({
        status: 'error',
        description: 'メニューの更新に失敗しました。',
      });
    },
  );

  useEffect(() => {
    void (async () => {
      if (selectedMenu) {
        const result = await crawlerApi.fetchCrawlers();
        if (result.ok) {
          setCralwers(result.value);
        } else {
          toast({
            status: 'error',
            description: `クローラーの取得に失敗しました: ${result.err.message}`,
          });
          onCloseModal();
        }
      }
    })();
  }, [selectedMenu, toast, onCloseModal]);

  useEffect(() => {
    void (async () => {
      if (selectedMenu) {
        const res = await menuApi.fetchMenuCustomers(selectedMenu.id);
        if (res.ok) {
          setMenuCustomers(res.value);
        } else {
          toast({
            status: 'error',
            description: `メニューに紐づく顧客の取得に失敗しました: ${res.err.message}`,
          });
        }
      }
    })();
  }, [selectedMenu, toast]);

  return {
    /** 選択中のメニュー */
    selectedMenu,
    /** メニュー編集モーダルのステップ */
    currentModalStep,
    /** メニュー編集モーダルの表示状態 */
    isOpenModal: !!selectedMenu,
    /** メニュー詳細を閉じる */
    onCloseModal,
    /** 編集完了後モーダルを閉じる */
    onCloseModalWhenCompleted,
    /** 確認画面へ */
    onConfirm,
    /** 入力画面へ戻る */
    onGoBack,
    /** 入力を送信する */
    onSubmit,
    /** フォーム */
    form,
    /** フォーム values */
    values: getValues(),
    /** メニュー削除確認モーダルを表示 */
    onOpenModalDelete,
    /** 削除可能フラグ */
    canDelete,
    /** クローラー一覧 */
    crawlers,
    /** メニューに紐づくクローラー一覧 */
    menuCrawlers,
    /** クローラー追加 */
    onAddCrawler,
    /** クローラー削除 */
    onDeleteCrawler,
    /** クローラー並び替え */
    onSortCrawler,
    /** メニューに紐づく顧客情報 */
    menuCustomers,
  };
};
